import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '@/lib/api';

export interface RegionalConfig {
  timezone: string;
  locale: string;
  date_format: string;
  time_format: '24h' | '12h';
  currency_code: string;
  currency_symbol: string;
  currency_symbol_position: 'before' | 'after';
  decimal_separator: string;
  thousands_separator: string;
  decimal_places: number;
}

const DEFAULT: RegionalConfig = {
  timezone: 'Africa/Johannesburg',
  locale: 'en-ZA',
  date_format: 'DD/MM/YYYY',
  time_format: '24h',
  currency_code: 'ZAR',
  currency_symbol: 'R',
  currency_symbol_position: 'before',
  decimal_separator: ',',
  thousands_separator: ' ',
  decimal_places: 2,
};

const CACHE_KEY = 'raptor:regional:v1';
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes — regional settings change very rarely, so this drastically cuts the /settings/regional call rate on route changes.

const readCache = (): RegionalConfig | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; data: RegionalConfig };
    if (!parsed?.at || Date.now() - parsed.at > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
};
const writeCache = (data: RegionalConfig): void => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data })); } catch { /* quota exceeded, ignore */ }
};

/** Drop the cached regional config so the next load refetches it — called
 * on login / tenant switch / logout so settings changes made by one user
 * (or a different tenant) never linger in another session. */
export const resetRegionalCache = (): void => {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
};

const Ctx = createContext<{ regional: RegionalConfig; reload: () => Promise<void> }>({
  regional: DEFAULT,
  reload: async () => {},
});

export const RegionalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Seed from cache so first paint doesn't wait on a network round-trip.
  // Falls back to DEFAULT then hydrates from the API in the background.
  const [regional, setRegional] = useState<RegionalConfig>(() => readCache() ?? DEFAULT);
  const reload = async (): Promise<void> => {
    try {
      const r = await api.get('/settings/regional');
      const merged = { ...DEFAULT, ...r.data };
      setRegional(merged);
      writeCache(merged);
    } catch { /* not authenticated yet, keep defaults */ }
  };
  // Seed from cache so first paint doesn't wait on a network round-trip,
  // but ALWAYS revalidate from the API in the background (stale-while-
  // revalidate). This guarantees a settings change made by another user
  // shows up on the next load/refresh instead of lingering behind the
  // localStorage cache for up to the TTL.
  useEffect(() => {
    void reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  return <Ctx.Provider value={{ regional, reload }}>{children}</Ctx.Provider>;
};

export const useRegional = () => useContext(Ctx);

export const formatCurrency = (amount: number, r: RegionalConfig): string => {
  const [intPart, decPart] = amount.toFixed(r.decimal_places).split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, r.thousands_separator);
  const num = r.decimal_places > 0 ? `${withThousands}${r.decimal_separator}${decPart}` : withThousands;
  return r.currency_symbol_position === 'before' ? `${r.currency_symbol}\u00A0${num}` : `${num}\u00A0${r.currency_symbol}`;
};

export const formatDate = (iso: string | Date | null | undefined, r: RegionalConfig): string => {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(r.locale, {
      timeZone: r.timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      hour12: r.time_format === '12h',
    }).format(d);
  } catch {
    return d.toISOString();
  }
};
